import React, { useState, useEffect } from 'react';
import { CheckCircle2, WalletCards } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { StatusMessage } from '../../ui/StatusMessage';

const WalletSelector = ({ onSelect, selectedWallet = '' }) => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    let isMounted = true;

    const fetchWallets = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        });

        const response = await fetch(`${apiUrl}/api/UserWallets`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Unable to load wallets (${response.status})`);
        }

        const data = await response.json();
        if (isMounted) {
          setWallets(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching wallets:', error);
        if (isMounted) {
          setWallets([]);
          setError('Could not load wallets. Please retry before buying.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchWallets();

    return () => {
      isMounted = false;
    };
  }, [apiUrl, getAccessTokenSilently]);

  if (loading) {
    return (
      <StatusMessage
        tone="loading"
        title="Loading wallets"
        message="We are loading your available payment wallets."
      />
    );
  }

  if (error) {
    return (
      <StatusMessage
        tone="error"
        title="Could not load wallets"
        message={error}
      />
    );
  }

  if (wallets.length === 0) {
    return (
      <StatusMessage
        tone="empty"
        title="No wallets available"
        message="Create or fund a wallet before buying this listing."
      />
    );
  }

  return (
    <div className="marketplace-wallet-grid" role="listbox" aria-label="Available wallets">
      {wallets.map((wallet) => (
        <button
          key={wallet.walletAddress}
          type="button"
          className={selectedWallet === wallet.walletAddress ? 'marketplace-wallet-card is-selected' : 'marketplace-wallet-card'}
          onClick={() => onSelect(wallet.walletAddress)}
          role="option"
          aria-selected={selectedWallet === wallet.walletAddress}
        >
          <span className="marketplace-wallet-icon">
            {selectedWallet === wallet.walletAddress ? <CheckCircle2 className="h-5 w-5" /> : <WalletCards className="h-5 w-5" />}
          </span>
          <span className="marketplace-wallet-main">
            <strong>{wallet.walletAddress}</strong>
            <span>Available balance: {wallet.balance}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default WalletSelector;
